import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CustomAlert, CustomSelect, PasswordField } from 'components/shared';
import { UserFormAction } from 'pages/user/user_form';
import { useAuth } from 'contexts/auth/auth_context';

interface LoginDataFormProps {
  groupOptions: { data: { uuid: string; name: string }[] };
  profileOptions: { data: { uuid: string; name: string }[] };
  permissionOptions: { data: { uuid: string; name: string }[] };
  institutionOptions: { data: { uuid: string; name: string }[] };
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  userFormAction: UserFormAction;
  userPermission?: string;
  permissionViewPassword?: boolean;
}

enum UserPermission {
  ADMINISTRADOR = 'ADMINISTRADOR',
  REPRESENTANTE_LEGAL_ESTADUAL = 'REPRESENTANTE LEGAL ESTADUAL',
  GERENTE_ESTADUAL = 'GERENTE ESTADUAL',
  REPRESENTANTE_LEGAL_MUNICIPAL = 'REPRESENTANTE LEGAL MUNICIPAL',
  GERENTE_MUNICIPAL = 'GERENTE MUNICIPAL',
  OPERADOR_MUNICIPAL = 'OPERADOR MUNICIPAL',
  CONSELHEIRO_MUNICIPAL = 'CONSELHEIRO MUNICIPAL',
  CONSELHEIRO_ESTADUAL = 'CONSELHEIRO ESTADUAL',
  OPERADOR_ESTADUAL = 'OPERADOR ESTADUAL',
}

export const getErrorMessage = (errors: any, touched: any) => {
  if (errors.loginData?.group && touched.loginData?.group) {
    return 'Selecione a opção que deseja do "Grupo do Perfil" abaixo para prosseguir.';
  }
  if (errors.loginData?.profile && touched.loginData?.profile) {
    return 'Selecione a opção que deseja do "Perfil do Usuário" abaixo para prosseguir.';
  }
  if (errors.loginData?.permission && touched.loginData?.permission) {
    return 'Selecione a opção que deseja da "Permissão do Usuário" abaixo para prosseguir.';
  }
  return null;
};

const LoginDataForm: React.FC<LoginDataFormProps> = ({
  groupOptions,
  profileOptions,
  permissionOptions,
  setFieldValue,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  userPermission,
  permissionViewPassword = false,
}) => {
  const errorMessage = getErrorMessage(errors, touched);

  const { user } = useAuth();
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);
  const [hasSetDefaultPermission, setHasSetDefaultPermission] = useState(false);
  const [lastAutoSelectedGroup, setLastAutoSelectedGroup] = useState<string | null>(null);


  const getSelectedName = (
    options: { data: { uuid: string; name: string }[] },
    uuid?: string
  ) => options?.data.find(o => o.uuid === uuid)?.name?.toUpperCase() ?? '';

  /**
   * Encontra o UUID de uma opção pelo nome
   */
  const getUuidByName = (
    options: { data: { uuid: string; name: string }[] },
    name: string
  ): string | undefined => {
    return options?.data.find(o => o.name.toUpperCase().includes(name.toUpperCase()))?.uuid;
  };

  const selectedGroupName = getSelectedName(groupOptions, values?.loginData?.group);
  const selectedProfileName = getSelectedName(profileOptions, values?.loginData?.profile);

  const isMunicipalSelection =
    selectedGroupName === 'MUNICIPAL' ||
    selectedProfileName.includes('MUNICIPAL') ||
    selectedProfileName.includes('PREFEITURA');

  const isEstadualSelection =
    selectedGroupName === 'ESTADUAL' ||
    selectedProfileName.includes('ESTADUAL') ||
    selectedProfileName.includes('SECRETARIA DE ESTADO');

  /**
   * Inicializa com valores padrão (MUNICIPAL e PREFEITURA MUNICIPAL) quando for novo cadastro
   */
  useEffect(() => {
    if (!hasInitializedDefaults && groupOptions?.data?.length > 0 && profileOptions?.data?.length > 0) {
      // Se grupo e perfil estão vazios, significa que é um novo cadastro
      if (!values?.loginData?.group && !values?.loginData?.profile) {
        const municipalGroupUuid = getUuidByName(groupOptions, 'MUNICIPAL');
        const prefeituraMunicipalUuid = getUuidByName(profileOptions, 'PREFEITURA MUNICIPAL');

        if (municipalGroupUuid) {
          setFieldValue('loginData.group', municipalGroupUuid);
        }
        if (prefeituraMunicipalUuid) {
          setFieldValue('loginData.profile', prefeituraMunicipalUuid);
        }

        setHasInitializedDefaults(true);
      } else if (values?.loginData?.group && !values?.loginData?.profile) {
        // Se só o grupo tiver valor, marca como inicializado
        setHasInitializedDefaults(true);
      } else if (values?.loginData?.group || values?.loginData?.profile) {
        // Se já tem algum valor, marca como inicializado
        setHasInitializedDefaults(true);
      }
    }
  }, [
    groupOptions?.data?.length,
    profileOptions?.data?.length,
    hasInitializedDefaults,
    values?.loginData?.group,
    values?.loginData?.profile,
    setFieldValue,
    groupOptions,
    profileOptions,
  ]);

  /**
   * Pré-seleciona "GERENTE MUNICIPAL" quando grupo e perfil são MUNICIPAL + PREFEITURA MUNICIPAL
   * e as permissões já foram carregadas (apenas uma vez, ao abrir a tela de cadastro).
   */
  useEffect(() => {
    if (
      hasSetDefaultPermission ||
      !permissionOptions?.data?.length ||
      values?.loginData?.permission
    ) {
      return;
    }
    const groupName = getSelectedName(groupOptions, values?.loginData?.group);
    const profileName = getSelectedName(profileOptions, values?.loginData?.profile);
    const isMunicipalPrefeitura =
      groupName === 'MUNICIPAL' && profileName.toUpperCase().includes('PREFEITURA');
    if (!isMunicipalPrefeitura) return;

    const gerenteMunicipalUuid = getUuidByName(permissionOptions, 'GERENTE MUNICIPAL');
    if (gerenteMunicipalUuid) {
      setFieldValue('loginData.permission', gerenteMunicipalUuid);
      setHasSetDefaultPermission(true);
    }
  }, [
    permissionOptions?.data?.length,
    permissionOptions,
    values?.loginData?.group,
    values?.loginData?.profile,
    values?.loginData?.permission,
    hasSetDefaultPermission,
    setFieldValue,
    groupOptions,
    profileOptions,
  ]);

  /**
   * Automação: quando o grupo mudar para ESTADUAL, muda o perfil para SECRETARIA DE ESTADO DE AGRICULTURA FAMILIAR
   * Só auto-seleciona quando o grupo muda, não quando o usuário muda o perfil manualmente.
   */
  useEffect(() => {
    const currentGroup = values?.loginData?.group;

    if (selectedGroupName === 'ESTADUAL' && currentGroup && lastAutoSelectedGroup !== currentGroup) {
      const estadualProfileUuid = getUuidByName(profileOptions, 'SECRETARIA DE ESTADO DE AGRICULTURA FAMILIAR');

      if (estadualProfileUuid && values?.loginData?.profile !== estadualProfileUuid) {
        setFieldValue('loginData.profile', estadualProfileUuid);
      }
      setLastAutoSelectedGroup(currentGroup);
    } else if (selectedGroupName === 'MUNICIPAL' && currentGroup && lastAutoSelectedGroup !== currentGroup && !values?.loginData?.profile) {
      const prefeituraMunicipalUuid = getUuidByName(profileOptions, 'PREFEITURA MUNICIPAL');
      if (prefeituraMunicipalUuid) {
        setFieldValue('loginData.profile', prefeituraMunicipalUuid);
      }
      setLastAutoSelectedGroup(currentGroup);
    } else if (currentGroup && lastAutoSelectedGroup !== currentGroup) {
      setLastAutoSelectedGroup(currentGroup);
    }
  }, [
    selectedGroupName,
    profileOptions,
    values?.loginData?.group,
    setFieldValue,
    lastAutoSelectedGroup,
  ]);


  const shouldShowPermission = (permName: string) => {
    const n = (permName || '').toUpperCase();

    if (isMunicipalSelection) {
      if (n.includes('ADMINISTRADOR')) return false;
      if (n.includes('ESTADUAL') || n.includes('ESTADO')) return false;
      return true;
    }
    if (isEstadualSelection) {
      return true;
    }

    return true;
  };


  const listBlockMunicipal = [
    UserPermission.GERENTE_MUNICIPAL,
    UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL,
  ];


  const isProfileDisabled = () => {
    if (!userPermission || !(userPermission in UserPermission)) {
      return false;
    }

    const permissionUser = permissionOptions.data.find(
      (option) => option.name === userPermission
    )?.name;

    const permissionName = permissionUser ?? UserPermission.ADMINISTRADOR;


    if (selectedGroupName === 'ESTADUAL' || selectedGroupName === 'MUNICIPAL') {
      return false;
    }
    const isPermissionRestricted = (restrictedPermissions: string[], permissionName: string) => {
      return restrictedPermissions.includes(permissionName);
    };

    const estadualRestrictedPermissions = [
      'GERENTE ESTADUAL',
      'OPERADOR ESTADUAL',
      'CONSELHEIRO ESTADUAL'
    ];

    const municipalRestrictedPermissions = [
      'GERENTE MUNICIPAL',
      'REPRESENTANTE LEGAL MUNICIPAL'
    ];

    const generalPermissions = [
      UserPermission.ADMINISTRADOR,
      UserPermission.REPRESENTANTE_LEGAL_ESTADUAL,
      UserPermission.GERENTE_ESTADUAL,
      UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL,
      UserPermission.GERENTE_MUNICIPAL
    ];

    if (generalPermissions.includes(userPermission as UserPermission)) {
      return false;
    }

    if (estadualRestrictedPermissions.includes(userPermission)) {
      return isPermissionRestricted(estadualRestrictedPermissions, permissionName);
    }

    if (municipalRestrictedPermissions.includes(userPermission)) {
      return isPermissionRestricted(municipalRestrictedPermissions, permissionName);
    }

    if (['OPERADOR MUNICIPAL', 'CONSELHEIRO MUNICIPAL'].includes(userPermission)) {
      return true;
    }

    return false;
  };




  const isDisabled = isProfileDisabled();

  const notEditPermission = (permission: string) => {
    const notEditPermissions = [
      UserPermission.CONSELHEIRO_MUNICIPAL,
      UserPermission.OPERADOR_MUNICIPAL,
      UserPermission.CONSELHEIRO_ESTADUAL,
      UserPermission.OPERADOR_ESTADUAL,
    ];

    return notEditPermissions.includes(permission as UserPermission);
  };

  const isNotEditPermission = notEditPermission(user?.permission.name as any);

  useEffect(() => {
    if (!user) return;

    if (
      user?.permission.name === UserPermission.GERENTE_MUNICIPAL ||
      user?.permission.name === UserPermission.REPRESENTANTE_LEGAL_MUNICIPAL
    ) {
      const groupMunicipio = groupOptions.data.find(
        (group) => group.name === 'MUNICIPAL',
      );

      setFieldValue('loginData.group', groupMunicipio?.uuid);
    }
  }, [groupOptions, user]);


  return (
    <Box sx={{ width: '1152px', marginTop: '20px' }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='h6'>Dados de login</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {errorMessage && (
            <CustomAlert severity='error' message={errorMessage} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <FormControl size='small' fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Grupo do Perfil
                </InputLabel>
                <Field
                  name='loginData.group'
                  as={CustomSelect}
                  labelId='group-label'
                  id='group-select'
                  label='Grupo do Perfil'
                  size='small'
                  value={values.loginData.group}
                  disabled={
                    (user &&
                      listBlockMunicipal.includes(userPermission as any)) ||
                    isNotEditPermission
                  }
                  onChange={(e: any) => {
                    const selectedValue = e.target.value;
                    const selectedGroup = groupOptions?.data?.find(g => g.uuid === selectedValue);
                    const groupName = selectedGroup?.name.toUpperCase() ?? '';

                    setFieldValue('loginData.group', selectedValue);

                    // Auto-select appropriate profile based on group selection
                    if (groupName.includes('MUNICIPAL')) {
                      const prefeituraMunicipalUuid = getUuidByName(profileOptions, 'PREFEITURA MUNICIPAL');
                      if (prefeituraMunicipalUuid) {
                        setFieldValue('loginData.profile', prefeituraMunicipalUuid);
                      }
                    } else if (groupName.includes('ESTADUAL')) {
                      const estadualProfileUuid = getUuidByName(profileOptions, 'SECRETARIA DE ESTADO DE AGRICULTURA FAMILIAR');
                      if (estadualProfileUuid) {
                        setFieldValue('loginData.profile', estadualProfileUuid);
                      }
                    } else {
                      setFieldValue('loginData.profile', '');
                    }

                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={
                    touched.loginData?.group && Boolean(errors.loginData?.group)
                  }
                >
                  {groupOptions?.data?.map((option) => {
                    return (
                      <MenuItem
                        key={option.uuid}
                        value={option.uuid}
                        sx={{
                          fontFamily: 'Roboto',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          textAlign: 'left',
                        }}
                      >
                        {option.name}
                      </MenuItem>
                    );
                  })}
                </Field>

                {touched.loginData?.group && errors.loginData?.group && (
                  <Typography
                    color='error'
                    variant='caption'
                    textAlign={'left'}
                  >
                    {errors.loginData?.group}
                  </Typography>
                )}
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Perfil do Usuário
                </InputLabel>
                <Field
                  name="loginData.profile"
                  as={CustomSelect}
                  labelId="profile-label"
                  id="profile-select"
                  label="Perfil do Usuário"
                  size="small"
                  value={values.loginData.profile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isDisabled}
                  error={touched.loginData?.profile && Boolean(errors.loginData?.profile)}
                >
                  {profileOptions?.data.map((option) => {
                    const optionName = option.name.toUpperCase();

                    if (isEstadualSelection) {
                      // Exclude MUNICIPAL/PREFEITURA profiles from ESTADUAL selection
                      if (
                        optionName.includes('PREFEITURA') ||
                        (optionName.includes('MUNICIPAL') && !optionName.includes('SECRETARIA'))
                      ) {
                        return null;
                      }
                      return (
                        <MenuItem key={option.uuid} value={option.uuid}>
                          {option.name}
                        </MenuItem>
                      );
                    }
                    if (isMunicipalSelection) {
                      // Exclude ESTADUAL/SECRETARIA DE ESTADO profiles from MUNICIPAL selection
                      if (
                        optionName.includes('SECRETARIA DE ESTADO') ||
                        (optionName.includes('ESTADUAL') && !optionName.includes('MUNICIPAL'))
                      ) {
                        return null;
                      }
                      return (
                        <MenuItem key={option.uuid} value={option.uuid}>
                          {option.name}
                        </MenuItem>
                      );
                    }

                    return null;
                  })}
                </Field>


                {touched.loginData?.profile && errors.loginData?.profile && (
                  <Typography color="error" variant="caption" textAlign={'left'}>
                    {errors.loginData?.profile}
                  </Typography>
                )}
              </FormControl>

            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
              <FormControl size='small' fullWidth>
                <InputLabel
                  sx={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    fontWeight: 400,
                    letterSpacing: '0.15px',
                    textAlign: 'left',
                    color: '#00000099',
                    '&.Mui-focused': {
                      color: '#00000099',
                    },
                  }}
                >
                  Permissão do Usuário
                </InputLabel>
                <Field
                  name="loginData.permission"
                  as={CustomSelect}
                  labelId="permission-label"
                  id="permission-select"
                  label="Permissão do Usuário"
                  size="small"
                  value={values.loginData.permission}
                  onChange={handleChange}
                  disabled={isDisabled || isNotEditPermission}
                  onBlur={handleBlur}
                  error={touched.loginData?.permission && Boolean(errors.loginData?.permission)}
                >
                  {permissionOptions?.data.map((option) => {
                    if (isEstadualSelection && shouldShowPermission(option.name)) {
                      return (
                        <MenuItem key={option.uuid} value={option.uuid}>
                          {option.name}
                        </MenuItem>
                      );
                    }

                    if (!isEstadualSelection && shouldShowPermission(option.name)) {
                      return (
                        <MenuItem key={option.uuid} value={option.uuid}>
                          {option.name}
                        </MenuItem>
                      );
                    }

                    return null;
                  })}
                </Field>


                {touched.loginData?.permission &&
                  errors.loginData?.permission && (
                    <Typography
                      color='error'
                      variant='caption'
                      textAlign={'left'}
                    >
                      {errors.loginData?.permission}
                    </Typography>
                  )}
              </FormControl>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default LoginDataForm;
